import dayjs, { type Dayjs } from "dayjs"

import {
  type Employment,
  Interruption,
  Position,
  type Movement,
  type Appointment,
  type Employee,
} from "@/graphql/types"
import { type MappedEmployment } from "@/store/employment"
import {
  EmploymentCategory,
  EmploymentEnum,
} from "@/views/personal/employments/EmploymentTableConfig"

import { getColorForTermin } from "./colors"
import { getOptions } from "./composables/useDropdown"
import { isDateRangeOverlap } from "./date"
import { type SubsidyStatus } from "./kibig/employees"

type MovementExtended = Movement & { localEntry?: string; localExit?: string }
type EmploymentExtended = MappedEmployment & { movement: MovementExtended }
export type MonthlySummaryEntry = {
  creditableWorkingHours: number
  entry: string
  exit: string
}
export type AppointmentExtended = Appointment & { localFrom?: string; localTo?: string }
export type EmployeeMonthlyHourItem = {
  entry: string
  exit: string
  creditableWorkingHours: number
  position?: Position
  status: SubsidyStatus
}

export const categories: EmploymentCategory[] = [
  EmploymentCategory.F,
  EmploymentCategory.E,
  EmploymentCategory.Z,
  EmploymentCategory.S,
  EmploymentCategory.A,
  EmploymentCategory.P,
]

export function extendEmployment(
  employment: Employment,
  yearStart: dayjs.Dayjs,
  yearEnd: dayjs.Dayjs
): EmploymentExtended {
  const entry = dayjs(employment.movement.entry)
  const exit = employment.movement.exit ? dayjs(employment.movement.exit) : false

  const movementExtended: MovementExtended = { ...employment.movement }

  if (entry.isBefore(yearStart)) {
    movementExtended.localEntry = yearStart.format("YYYY-MM-DD")
  }

  if (!exit || exit.isAfter(yearEnd)) {
    movementExtended.localExit = yearEnd.format("YYYY-MM-DD")
  }

  return { ...toMapped(employment), movement: movementExtended }
}
export function toMapped(employment: Employment): MappedEmployment {
  const category = mapPositionToCategory(employment.position)
  const qualification = mapPositionToKibigQualification(employment.position)
  return {
    ...employment,
    ...(employment?.employee?.person
      ? {
          fullname: `${employment?.employee?.person?.firstname} ${employment?.employee?.person?.lastname}`,
        }
      : {}),
    movement: {
      ...employment.movement,
      ...(employment?.movement?.entry ? { entry: new Date(employment.movement.entry) } : {}),
      ...(employment?.movement?.exit ? { exit: new Date(employment.movement.exit) } : {}),
    },
    category,
    f_e: category === EmploymentCategory.F ? "F" : category === EmploymentCategory.E ? "E" : "",
    qualification,
    workingHoursFixed: employment.workingHours?.toFixed(2),
    employmentType: employment?.interruption ? EmploymentEnum.I : EmploymentEnum.P,
    employeeCategories: (employment.employee?.categories ?? []).map((c) => c.category?.id),
  }
}

export function sortEmploymentsFromOldToNew(e1: Employment, e2: Employment) {
  return dayjs(e1.movement.entry).diff(e2.movement.entry, "days")
}

export function sortEmploymentsFromNewToOld(e1: Employment, e2: Employment) {
  return dayjs(e2.movement.entry).diff(e1.movement.entry, "days")
}

export function mapPositionToCategory(type: Position | null | undefined): EmploymentCategory {
  switch (type) {
    case Position.E:
    case Position.S:
    case Position.H:
    case Position.P:
    case Position.A:
      return EmploymentCategory.F
    case Position.K:
    case Position.B:
    case Position.G:
    case Position.O:
      return EmploymentCategory.E
    case Position.Z:
      return EmploymentCategory.Z
    case Position.Y:
      return EmploymentCategory.S
    case Position.T:
      return EmploymentCategory.A
    case Position.V:
    case Position.N:
    case Position.W:
    case Position.D:
    case Position.J:
    case Position.F:
      return EmploymentCategory.P
    case Position.U:
    case undefined:
    case null:
    default:
      return EmploymentCategory.N
  }
}

// Financially supported qualifications (not including optional "Personalbonus")
export function isKibigQualification(pos: Position | null | undefined): boolean {
  const qual = mapPositionToKibigQualification(pos)
  return qual === KibigQualification.Fachkraft || qual === KibigQualification.Ergaenzungskraft
}

/** KiBiG qualifications */
export enum KibigQualification {
  KeineBelegung = 0,
  Fachkraft = 1,
  Ergaenzungskraft = 2,
  Zusatzkraft = 3,
  /** @deprecated since July 2023 in favor of "Sprachfachkraft" */
  Schwerpunktkraft = 4,
  Tagespflegeperson = 5,
  Assistenzkraft = 6,
  PädagogischesPersonal = 7,
  HauswirtschaftlichesPersonal = 8,
  Verwaltungsfachkraft = 9,
  /** only to be used >= July 2023 */
  Sprachfachkraft = 10,
  PraktikantImSEJ = 11,
}

export enum DisplayModes {
  ALL = "all",
  EMPLOYMENTS = "employments",
  MONTHLY_HOURS = "monthly_hours",
  ABSENCES = "ABSENCES",
}

export enum Types {
  B = "B",
  E = "E",
  C = "C",
  F = "F",
  G = "G",
  L = "L",
  K = "K",
  M = "M",
  R = "R",
  A = "A",
  S = "S",
  W = "W",
  U = "U",
  Y = "Y",
  Z = "Z",
}

export const typeOptions = getOptions(Types, "personal.termine.page.types")

export function mapPositionToKibigQualification(
  type: Position | Interruption | null | undefined
): KibigQualification {
  switch (type) {
    case Position.E:
    case Position.S:
    case Position.H:
    case Position.P:
    case Position.A:
      return KibigQualification.Fachkraft
    case Position.K:
    case Position.B:
    case Position.G:
    case Position.O:
      return KibigQualification.Ergaenzungskraft
    case Position.Z:
      return KibigQualification.Zusatzkraft
    // "Schwerpunktkraft" was used until June 2023:
    //case Position.Y:
    //  return Qualification.Schwerpunktkraft
    // Not supported in adebisKITA
    //case Symbol() as never:
    //  return KibigQualification.Tagespflegeperson
    case Position.T:
      return KibigQualification.Assistenzkraft
    case Position.D:
    case Position.F:
      return KibigQualification.PädagogischesPersonal
    case Position.N:
      return KibigQualification.HauswirtschaftlichesPersonal
    case Position.W:
      return KibigQualification.Verwaltungsfachkraft
    case Position.Y:
      return KibigQualification.Sprachfachkraft
    case Position.J:
      return KibigQualification.PraktikantImSEJ
    case Interruption.I2:
    case Interruption.I3:
    case Interruption.I4:
    case Interruption.I5:
    case Position.U:
    case Position.V:
    case undefined:
    case null:
    default:
      return KibigQualification.KeineBelegung
  }
}

export function getAppointmentsForEmployee(employee: Employee) {
  return (employee.appointments ?? []).reduce((acc: Appointment[], next) => {
    if (next?.appointment) acc.push(next.appointment)
    return acc
  }, [])
}

export function getAbsenceDays(employee: Employee, yearStart: Dayjs, yearEnd: Dayjs) {
  return getAppointmentsForEmployee(employee)
    .filter((app: Appointment) => {
      if (!app?.absence) return false

      const rangeStart = app.from
      const rangeEnd = app.to ?? app.from

      return isDateRangeOverlap(rangeStart, rangeEnd, yearStart, yearEnd)
    })
    .map((app) => extendAppointment(app, yearStart, yearEnd))
}
export function extendAppointment(
  app: Appointment,
  yearStart: Dayjs,
  yearEnd: Dayjs
): AppointmentExtended {
  const from = dayjs(app.from)
  const to = !app.to ? from : dayjs(app.to)

  const appointmentExtended: AppointmentExtended = { ...app }

  if (from.isBefore(yearStart)) {
    appointmentExtended.localFrom = yearStart.format("YYYY-MM-DD")
  }

  if (to.isAfter(yearEnd)) {
    appointmentExtended.localTo = yearEnd.format("YYYY-MM-DD")
  }

  return appointmentExtended
}

export function getAbsenceDaysBg(type: string, displayMode: DisplayModes, isDark: boolean) {
  if (displayMode !== DisplayModes.ABSENCES) return "red"
  return getColorForTermin(type, isDark) ?? "red"
}
